import React, { useEffect, useState, useRef } from "react";
import { Card, Spinner, Alert } from "react-bootstrap";
import api from "../../config/ApiConfig";

const TrackingMap = ({ orderId }) => {
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markersRef = useRef({ driver: null, destination: null });

  // Polling interval (refresh every 30 seconds)
  const POLLING_INTERVAL = 30000;

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }

    const fetchTrackingData = async () => {
      try {
        // Use the authenticated tracking endpoint
        const response = await api.get(`/fo/commande/${orderId}/tracking`);

        if (response.data.success) {
          setTrackingData(response.data);
          setError(null);
        } else {
          setError(response.data.message || "Impossible de récupérer les données de tracking");
        }
      } catch (err) {
        console.error("Error fetching tracking data:", err);
        setError(err.response?.data?.message || "Erreur lors de la récupération des données de tracking");
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchTrackingData();

    // Setup polling
    const intervalId = setInterval(fetchTrackingData, POLLING_INTERVAL);

    return () => clearInterval(intervalId);
  }, [orderId]);

  useEffect(() => {
    if (!trackingData?.delivery?.current_position || !mapRef.current) {
      return;
    }

    // Initialize Google Maps
    const initMap = () => {
      const { current_position, destination } = trackingData.delivery;

      // Create map centered on driver's position
      const map = new window.google.maps.Map(mapRef.current, {
        center: {
          lat: parseFloat(current_position.latitude),
          lng: parseFloat(current_position.longitude),
        },
        zoom: 13,
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }],
          },
        ],
      });

      googleMapRef.current = map;

      // Driver marker (truck icon)
      if (markersRef.current.driver) {
        markersRef.current.driver.setMap(null);
      }
      markersRef.current.driver = new window.google.maps.Marker({
        position: {
          lat: parseFloat(current_position.latitude),
          lng: parseFloat(current_position.longitude),
        },
        map,
        icon: {
          url: "https://maps.google.com/mapfiles/ms/icons/truck.png",
          scaledSize: new window.google.maps.Size(40, 40),
        },
        title: trackingData.delivery.livreur?.name || "Livreur",
      });

      // Destination marker
      if (destination.latitude && destination.longitude) {
        if (markersRef.current.destination) {
          markersRef.current.destination.setMap(null);
        }
        markersRef.current.destination = new window.google.maps.Marker({
          position: {
            lat: parseFloat(destination.latitude),
            lng: parseFloat(destination.longitude),
          },
          map,
          icon: {
            url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
            scaledSize: new window.google.maps.Size(40, 40),
          },
          title: "Destination",
        });

        // Draw route line
        const routePath = new window.google.maps.Polyline({
          path: [
            {
              lat: parseFloat(current_position.latitude),
              lng: parseFloat(current_position.longitude),
            },
            {
              lat: parseFloat(destination.latitude),
              lng: parseFloat(destination.longitude),
            },
          ],
          geodesic: true,
          strokeColor: "#0d6efd",
          strokeOpacity: 0.7,
          strokeWeight: 3,
        });
        routePath.setMap(map);

        // Fit map to show both markers
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend({
          lat: parseFloat(current_position.latitude),
          lng: parseFloat(current_position.longitude),
        });
        bounds.extend({
          lat: parseFloat(destination.latitude),
          lng: parseFloat(destination.longitude),
        });
        map.fitBounds(bounds);

        // Add some padding
        const padding = { top: 50, right: 50, bottom: 50, left: 50 };
        map.fitBounds(bounds, padding);
      }
    };

    // Load Google Maps script if not already loaded
    if (!window.google) {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        console.error("Google Maps API key not found. Please set REACT_APP_GOOGLE_MAPS_API_KEY in .env");
        setError("Configuration de la carte manquante");
        return;
      }
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
      script.async = true;
      script.defer = true;
      script.onload = initMap;
      document.head.appendChild(script);
    } else {
      initMap();
    }

    // Cleanup
    return () => {
      if (markersRef.current.driver) {
        markersRef.current.driver.setMap(null);
      }
      if (markersRef.current.destination) {
        markersRef.current.destination.setMap(null);
      }
    };
  }, [trackingData]);

  if (!orderId) {
    return null; // Don't show anything if no order ID
  }

  if (loading) {
    return (
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0">
            <i className="fas fa-map-marked-alt me-2"></i>
            Suivi en temps réel
          </h5>
        </Card.Header>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Chargement de la carte...</span>
          </Spinner>
          <p className="text-muted mt-3">Chargement de la carte...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0">
            <i className="fas fa-map-marked-alt me-2"></i>
            Suivi en temps réel
          </h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  if (!trackingData?.delivery?.current_position) {
    return (
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0">
            <i className="fas fa-map-marked-alt me-2"></i>
            Suivi en temps réel
          </h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="info">
            <i className="fas fa-info-circle me-2"></i>
            Le suivi en temps réel sera disponible lorsque le livreur sera en route.
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  const { livreur, current_position, eta } = trackingData.delivery;

  return (
    <Card className="mb-4 shadow-sm">
      <Card.Header className="bg-primary text-white">
        <h5 className="mb-0">
          <i className="fas fa-map-marked-alt me-2"></i>
          Suivi en temps réel
        </h5>
      </Card.Header>
      <Card.Body className="p-0">
        {/* Map container */}
        <div
          ref={mapRef}
          style={{
            width: "100%",
            height: "400px",
            backgroundColor: "#e5e5e5",
          }}
        />

        {/* Tracking info */}
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <strong>
                <i className="fas fa-user-circle me-2"></i>
                {livreur?.name || "Livreur"}
              </strong>
              <div className="text-muted small mt-1">
                <i className="fas fa-circle text-success me-1" style={{ fontSize: '8px' }}></i>
                {livreur?.is_online ? "En ligne" : "Hors ligne"}
                {livreur?.status && ` - ${livreur.status}`}
              </div>
            </div>
            {eta && (
              <div className="text-end">
                <div className="text-muted small">Temps estimé</div>
                <strong className="text-primary">{eta}</strong>
              </div>
            )}
          </div>

          {current_position?.last_update && (
            <div className="text-muted small text-end">
              <i className="fas fa-clock me-1"></i>
              Dernière mise à jour: {new Date(current_position.last_update).toLocaleTimeString('fr-FR')}
            </div>
          )}

          <div className="alert alert-info mt-3 mb-0" role="alert">
            <i className="fas fa-info-circle me-2"></i>
            <small>La position est mise à jour automatiquement toutes les 30 secondes.</small>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default TrackingMap;
